import React, { useRef } from "react";

import { Button, Modal, Spinner } from "@amphore-dev/amphore-lib";
import { FormikProps } from "formik";
import { t } from "i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ViewsService from "@/services/ViewsService";

import { viewKeys } from "@/constants/CQueryKeys";

import { TView, TViewEditFormValues, TViewPayload } from "@/types";

import { ViewEditForm } from "@/forms/index";
import { promiseToast } from "@/modules/index";

export interface IViewFormModalProps {
	open: boolean;
	onClose: () => void;
	/** Editing this view; omitted = creating one. */
	view?: Pick<TView, "id">;
	onSaved?: (view: TView) => void;
}

/** The form's values as the API takes them: a selection is a list of ids. */
const toPayload = (values: TViewEditFormValues): TViewPayload => ({
	name: values.name ?? "",
	color: values.color,
	description: values.description,
	type: values.type,
	projects: (values.projects ?? []).map((project) => project.id),
	clients: (values.clients ?? []).map((client) => client.id),
});

/**
 * ViewEditForm in a modal, for the list page's "New" and the detail
 * page's "Edit". Editing fetches the view afresh, so the form never
 * starts from a stale list row - the same query the side panel uses.
 */
export const ViewFormModal: React.FC<IViewFormModalProps> = ({
	open,
	onClose,
	view,
	onSaved,
}) => {
	const queryClient = useQueryClient();
	const formRef = useRef<FormikProps<TViewEditFormValues>>(null);
	const isEditing = !!view;

	const { data: initialValues, isLoading } = useQuery({
		queryKey: viewKeys.edit(view?.id),
		queryFn: () => ViewsService.getView(view!.id),
		enabled: open && isEditing,
	});

	const mutation = useMutation({
		mutationFn: (values: TViewEditFormValues) =>
			isEditing
				? ViewsService.updateView(view.id, toPayload(values))
				: ViewsService.createView(toPayload(values)),
		onSuccess: (saved: TView) => {
			// "projects" too: what a view gathers is what its projects
			// card and its task lists answer.
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "views" ||
					query.queryKey[0] === "view" ||
					query.queryKey[0] === "projects",
			});
			onSaved?.(saved);
			onClose();
		},
	});

	const handleSubmit = async (values: TViewEditFormValues) => {
		const prom = mutation.mutateAsync(values);
		promiseToast(prom);
		// The toast reports the failure; the modal stays open for a retry.
		await prom.catch(() => undefined);
	};

	const i18n = isEditing ? "views.edit" : "views.create";

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={t(`${i18n}.title`)}
			size="lg"
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose}>
						{t("actions.cancel")}
					</Button>
					<Button
						color="primary"
						isLoading={mutation.isPending}
						disabled={isEditing && isLoading}
						onClick={() => formRef.current?.submitForm()}
					>
						{t(`${i18n}.submit`)}
					</Button>
				</div>
			}
		>
			{/* Mounted with the modal so each opening starts from the fetched values. */}
			{open && isEditing && isLoading && <Spinner className="mx-auto" />}
			{open && (!isEditing || initialValues) && (
				<ViewEditForm
					innerRef={formRef}
					initialValues={initialValues as TViewEditFormValues}
					onSubmit={handleSubmit}
				/>
			)}
		</Modal>
	);
};
